import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useScrollSpy } from '@/hooks/useScrollSpy';

const sectionHash = (section: string) => `#${encodeURIComponent(section.toLowerCase())}`;

/** Router-aware scroll spy with shareable section hashes and replace-only URL updates. */
export const useHashScrollSpy = (sections: readonly string[], root: HTMLElement | null) => {
    const navigation = useScrollSpy(sections, { root });
    const { activeSection, scrollToSection } = navigation;
    const location = useLocation();
    const navigate = useNavigate();
    const writtenHash = useRef<string | null>(null);
    const pendingSection = useRef<string | null>(null);
    const stopRestoring = useRef<(() => void) | null>(null);

    useEffect(() => () => stopRestoring.current?.(), [root]);

    useEffect(() => {
        if (!root || sections.length === 0) return;
        // Our own replace only updates the URL; it must not start another scroll.
        if (writtenHash.current === location.hash) {
            writtenHash.current = null;
            return;
        }
        writtenHash.current = null;
        stopRestoring.current?.();
        const section = sections.find((name) => sectionHash(name) === location.hash) ?? sections[0];
        pendingSection.current = section;
        const restore = () => scrollToSection(section, 'instant');
        restore();

        // Keep a direct link aligned while asynchronously loaded content above it changes height.
        // Once the user interacts, their scrolling/navigation takes precedence.
        const observer = new ResizeObserver(restore);
        if (root.firstElementChild) observer.observe(root.firstElementChild);
        const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
        const stop = () => {
            observer.disconnect();
            events.forEach((event) => root.removeEventListener(event, stop));
            stopRestoring.current = null;
            pendingSection.current = null;
        };
        events.forEach((event) => root.addEventListener(event, stop, { passive: true }));
        stopRestoring.current = stop;
    }, [location.hash, location.key, root, scrollToSection, sections]);

    useEffect(() => {
        if (!root || !activeSection) return;
        // Do not overwrite a deep link with the spy's initial selection before restoration completes.
        if (pendingSection.current && pendingSection.current !== activeSection) return;
        pendingSection.current = null;
        const hash = sectionHash(activeSection);
        if (location.hash === hash) return;
        writtenHash.current = hash;
        navigate(
            { pathname: location.pathname, search: location.search, hash },
            { replace: true, preventScrollReset: true, state: location.state },
        );
    }, [activeSection, location, navigate, root]);

    return navigation;
};
